/**
 * Business sources on the client (docs/proposals/business-sources.md).
 *
 * Each business connects its own mailboxes, as many as it sends from: Gmail /
 * Google Workspace and Zoho Mail, mixed freely. `business_sources()` returns
 * one row per mailbox (or one empty row for a business without any),
 * token-free; `groupByBusiness()` folds that into one entry per business.
 * Connect, sync and disconnect go through the edge functions with the
 * business id (and the mailbox's source_id) in the body, so a business can be
 * set up without switching the whole app to it.
 *
 * OAuth runs in a popup. The callback page postMessages the opener with
 * `{ type: 'google-oauth-success' | 'zoho-oauth-success' | '...-error', purpose }`;
 * `onSourceConnected` turns that into a callback.
 */

import { supabase } from './supabase'

export type EmailProvider = 'gmail' | 'zoho'
export type SourceStatus = 'none' | 'connected' | 'syncing' | 'error' | 'needs_reconnect'

export interface BusinessSource {
  workspace_id: string
  workspace_name: string
  workspace_kind: 'personal' | 'business'
  role: 'owner' | 'admin' | 'member'
  is_current: boolean
  /** The mailbox (gmail_connections.id or zoho_mail_connections.id); null on a business's empty row. */
  source_id: string | null
  provider: EmailProvider | null
  email: string | null
  status: SourceStatus
  needs_reconnect: boolean
  last_sync_at: string | null
  last_sync_error: string | null
  connected_by_me: boolean
  connected_at: string | null
}

export const PROVIDERS: Record<EmailProvider, { label: string; hint: string }> = {
  gmail: {
    label: 'Gmail or Google Workspace',
    hint: 'For @gmail.com addresses and company domains whose email runs on Google.',
  },
  zoho: {
    label: 'Zoho Mail',
    hint: 'For company domains whose email runs on Zoho (zoho.com, zoho.in, …).',
  },
}

/** Fired on `window` after a source is connected, switched or removed. */
export const SOURCES_CHANGED_EVENT = 'hunts:sources-changed'

const FUNCTIONS_URL = 'https://ouyzishoamkmehmqlnry.supabase.co/functions/v1'

function functionsUrl(): string {
  return (supabase as unknown as { functionsUrl?: string })?.functionsUrl ?? FUNCTIONS_URL
}

function asStatus(v: unknown): SourceStatus {
  return v === 'connected' || v === 'syncing' || v === 'error' || v === 'needs_reconnect'
    ? v
    : 'none'
}

export async function fetchBusinessSources(): Promise<BusinessSource[]> {
  const { data, error } = await supabase.rpc('business_sources')
  if (error) throw new Error(error.message)
  return ((data ?? []) as Array<Record<string, unknown>>).map((r) => ({
    workspace_id: String(r.workspace_id),
    workspace_name: String(r.workspace_name ?? ''),
    workspace_kind: r.workspace_kind === 'personal' ? 'personal' : 'business',
    role: (r.role as BusinessSource['role']) ?? 'member',
    is_current: r.is_current === true,
    source_id: typeof r.source_id === 'string' ? r.source_id : null,
    provider: r.provider === 'gmail' || r.provider === 'zoho' ? r.provider : null,
    email: typeof r.email === 'string' ? r.email : null,
    status: asStatus(r.status),
    needs_reconnect: r.needs_reconnect === true,
    last_sync_at: typeof r.last_sync_at === 'string' ? r.last_sync_at : null,
    last_sync_error: typeof r.last_sync_error === 'string' ? r.last_sync_error : null,
    connected_by_me: r.connected_by_me === true,
    connected_at: typeof r.connected_at === 'string' ? r.connected_at : null,
  }))
}

/** One business with all of its connected mailboxes. */
export interface BusinessEmail {
  workspace_id: string
  workspace_name: string
  workspace_kind: 'personal' | 'business'
  role: 'owner' | 'admin' | 'member'
  is_current: boolean
  mailboxes: BusinessSource[]
}

/** Fold business_sources() rows (one per mailbox) into one entry per business, in order. */
export function groupByBusiness(rows: BusinessSource[]): BusinessEmail[] {
  const out = new Map<string, BusinessEmail>()
  for (const r of rows) {
    let g = out.get(r.workspace_id)
    if (!g) {
      g = {
        workspace_id: r.workspace_id,
        workspace_name: r.workspace_name,
        workspace_kind: r.workspace_kind,
        role: r.role,
        is_current: r.is_current,
        mailboxes: [],
      }
      out.set(r.workspace_id, g)
    }
    if (r.provider && r.source_id) g.mailboxes.push(r)
  }
  return [...out.values()]
}

export function businessEmail(rows: BusinessSource[], workspaceId: string): BusinessEmail | null {
  return groupByBusiness(rows).find((g) => g.workspace_id === workspaceId) ?? null
}

/** One line for a business: its inboxes, or that it has none yet. */
export function describeBusinessEmail(g: BusinessEmail): string {
  const boxes = g.mailboxes
  if (boxes.length === 0) return 'No email connected'
  const problem = boxes.find((b) => b.status === 'needs_reconnect' || b.status === 'error')
  if (problem) return describeSource(problem)
  const first = boxes[0]!.email ?? PROVIDERS[boxes[0]!.provider!].label
  return boxes.length === 1 ? describeSource(boxes[0]!) : `${first} and ${boxes.length - 1} more`
}

/** POST to an edge function with the user's JWT; throws the function's `error` message. */
export async function callFunction(
  name: string,
  body: Record<string, unknown>,
): Promise<Record<string, unknown>> {
  const { data: sess } = await supabase.auth.getSession()
  const token = sess?.session?.access_token
  if (!token) throw new Error('Sign in first.')
  const res = await fetch(`${functionsUrl()}/${name}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  })
  const json = (await res.json().catch(() => ({}))) as Record<string, unknown>
  if (!res.ok || json.ok === false) {
    const msg = typeof json.error === 'string' ? json.error : `Request failed (HTTP ${res.status})`
    throw new Error(msg)
  }
  return json
}

/**
 * Start the OAuth consent for a business's mailbox in a popup. The popup is
 * opened before the network call so browsers treat it as user-initiated.
 */
export async function connectEmail(provider: EmailProvider, workspaceId: string): Promise<void> {
  const popup =
    typeof window !== 'undefined'
      ? window.open('', 'hunts_source_oauth', 'popup,width=520,height=680')
      : null
  try {
    const json =
      provider === 'gmail'
        ? await callFunction('google-oauth-init', { purpose: 'gmail', workspace_id: workspaceId })
        : await callFunction('zoho-oauth-init', { purpose: 'mail', workspace_id: workspaceId })
    const url = typeof json.authUrl === 'string' ? json.authUrl : null
    if (!url) throw new Error('No consent link came back. Try again.')
    if (popup && !popup.closed) {
      popup.location.href = url
    } else if (!window.open(url, 'hunts_source_oauth', 'popup,width=520,height=680')) {
      throw new Error(
        'Your browser blocked the sign-in window. Allow popups for this site and retry.',
      )
    }
  } catch (e) {
    try {
      popup?.close()
    } catch {
      // already closed
    }
    throw e
  }
}

export async function disconnectEmail(box: BusinessSource): Promise<void> {
  const ids = { workspace_id: box.workspace_id, source_id: box.source_id }
  if (box.provider === 'gmail') {
    await callFunction('google-disconnect', { purpose: 'gmail', ...ids })
  } else {
    await callFunction('zoho-mail-source', { action: 'disconnect', ...ids })
  }
  notifySourcesChanged()
}

/** The inbox a send goes out from, in the order gmail-send uses: my Gmail, then my Zoho, then the oldest. */
export function pickSendingBox(rows: BusinessSource[]): BusinessSource | null {
  const here = rows
    .filter((r) => r.is_current && r.provider)
    .sort((a, b) => (a.connected_at ?? '').localeCompare(b.connected_at ?? ''))
  return (
    here.find((r) => r.connected_by_me && r.provider === 'gmail') ??
    here.find((r) => r.connected_by_me && r.provider === 'zoho') ??
    here[0] ??
    null
  )
}

/** Pull new mail now. Returns how many messages were processed. */
export async function syncEmailNow(box: BusinessSource): Promise<number> {
  const ids = { workspace_id: box.workspace_id, source_id: box.source_id }
  const json =
    box.provider === 'gmail'
      ? await callFunction('gmail-sync', ids)
      : await callFunction('zoho-mail-source', { action: 'sync', ...ids })
  return typeof json.processed === 'number' ? json.processed : 0
}

export function notifySourcesChanged(): void {
  try {
    window.dispatchEvent(new CustomEvent(SOURCES_CHANGED_EVENT))
  } catch {
    // no window
  }
}

/**
 * Listen for the OAuth popup's result. Returns an unsubscribe function.
 * Only email purposes are handled here; calendar results stay with the
 * legacy Settings panel.
 */
export function onSourceConnected(
  cb: (result: { ok: boolean; provider: EmailProvider; message: string }) => void,
): () => void {
  const handler = (ev: MessageEvent) => {
    const d = ev?.data as { type?: unknown; purpose?: unknown; message?: unknown } | null
    if (!d || typeof d !== 'object' || typeof d.type !== 'string') return
    const message = typeof d.message === 'string' ? d.message : ''
    if (d.type.startsWith('google-oauth-') && d.purpose === 'gmail') {
      cb({ ok: d.type === 'google-oauth-success', provider: 'gmail', message })
    } else if (d.type.startsWith('zoho-oauth-') && d.purpose === 'mail') {
      cb({ ok: d.type === 'zoho-oauth-success', provider: 'zoho', message })
    }
  }
  window.addEventListener('message', handler)
  return () => window.removeEventListener('message', handler)
}

/** Short human status line for a source. */
export function describeSource(s: BusinessSource): string {
  if (!s.provider) return 'No email connected'
  const who = s.email ?? PROVIDERS[s.provider].label
  switch (s.status) {
    case 'needs_reconnect':
      return `${who} needs signing in again`
    case 'syncing':
      return `${who} is syncing`
    case 'error':
      return `${who} had a sync problem`
    default:
      return who
  }
}
